import { useState, useEffect, useRef, useMemo, MutableRefObject } from 'react';
import { WS_URL, READY } from '../constants';
import { ChatResponseFile, ClarificationQuestion, Message } from '../types';
import { MessageFormatter } from '../MessageFormatter';
import { getAccessToken } from '../../../Utils/auth';
import { generateChatTitle } from '../../../services/api/chatHistory.api';

// The reply's text as displayable markdown. formatStructuredData accepts any
// value: text comes out exactly as formatMessage would produce it, and
// anything else (an object, list or number, if the backend ever sends one)
// becomes a readable JSON block instead of reaching ReactMarkdown, which
// only accepts strings and would crash the app.
const toMessageContent = (data: any): string =>
  MessageFormatter.formatStructuredData(
    data.structured_data || data.ai_response || data.response || data.message || 'Received a message.'
  );

// An unexpected close is the only trace of some failures, such as a token
// rejected before the connection opens, which the browser reports only as a
// close. So it's reported; a normal close isn't.
const reportUnexpectedClose = (e: CloseEvent) => {
  if (!e.wasClean) console.warn('[useWebSocket] Connection closed unexpectedly:', e.code, e.reason);
};

export const useWebSocket = (
  email: string | null,
  userId: any,
  isNewChatRef: MutableRefObject<boolean>,
  onToggleDeepSearch?: (value: boolean) => void,
  onFilesReceived?: (files: ChatResponseFile[]) => void,
) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAssistantActive, setIsAssistantActive] = useState(false);
  const [thinkingSessionId, setThinkingSessionId] = useState<string | null>(null);
  const [thinkingText, setThinkingText] = useState<string>('');
  // Questions from a `clarification` message awaiting the user's answers —
  // non-null swaps the chat input for the clarification panel.
  const [pendingClarification, setPendingClarification] = useState<ClarificationQuestion[] | null>(null);

  const ws = useRef<WebSocket | null>(null);
  const lastUserRequestAtRef = useRef<number>(0);

  // The turn_id of whatever send is currently "active" — null when nothing
  // is (e.g. right after Stop). The backend echoes turn_id on every
  // per-turn message (logs/toggle/response), so a message is only accepted
  // if it matches this. This single ref replaces what used to be a separate
  // ignoreIncomingRef boolean: "is anything active" is just
  // `activeTurnIdRef.current !== null`, and "does this message belong to
  // the active turn" is the equality check below — one rule covers both
  // Stop-with-no-resend (ref is null, nothing matches) and Stop-then-resend
  // (ref moved to the new turn, so the old turn's stale messages — logs,
  // toggle, or response — no longer match and get dropped instead of
  // bleeding into the new turn).
  const activeTurnIdRef = useRef<string | null>(null);

  // Set when ui_dr_toggle_off arrives, which happens right before the final
  // deep-research report is sent (not after, despite what it sounds like).
  // The next response message consumes it and gets tagged as the report.
  const pendingDeepResearchReportRef = useRef(false);

  // Tracks the number of agent responses in the CURRENT session — reset by
  // Chat.tsx on every "leave this chat" transition (new chat, or switching
  // to a different history session; see its callers' comments), not
  // internally here, so that Chat.tsx can read the outgoing session's count
  // (to decide whether it qualifies for title generation) before zeroing it
  // — a single owner avoids a race between two independent 'chat:new'
  // listeners each racing to read/reset this first.
  const agentResponseCountRef = useRef(0);

  // ── Latest user message ID ────────────────────────────────────────────────
  // Walks messages in reverse to find the most recent user message.
  // Used by Chat.tsx to decide whether an edit/resend should truncate or append.
  const latestUserMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === "user") return messages[i].id;
    }
    return null;
  }, [messages]);

  // ── Incoming WebSocket message handler ────────────────────────────────────
  const handleSocketMessage = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      const type = (data.type || '').toLowerCase();

      // Keepalive heartbeat — {"type": "ping", "message": "connection is live"}.
      // Carries no chat content, just ignore it.
      if (type === 'ping') {
        return;
      }

      // Every per-turn message type carries turn_id — drop anything that
      // isn't for the currently active turn (superseded by Stop, or by a
      // newer send after Stop). See activeTurnIdRef's comment.
      if (
        (type === 'logs' || type === 'toggle' || type === 'response') &&
        data.turn_id !== activeTurnIdRef.current
      ) {
        return;
      }

      // Clarification carries no turn_id, so it can't be matched to a turn —
      // just drop it once nothing is active (Stop, new chat, session switch).
      if (type === 'clarification' && activeTurnIdRef.current === null) {
        return;
      }

      // ── Session ID handling ─────────────────────────────────────────────
      // The backend returns the session ID in the first response.
      // We store it in localStorage and dispatch a sidebar refresh exactly
      // once per conversation using the isNewSession flag.
      if (data.chat_session_id || data.session_id) {
        const sid = data.chat_session_id ?? data.session_id;

        // Only dispatch if this is the first time we're seeing a session ID.
        // localStorage won't have it yet on the first response so isNewSession
        // is true exactly once. Every subsequent message in the same session
        // already has the ID stored, preventing redundant API calls.
        const isNewSession = !localStorage.getItem('chat_session_id');

        localStorage.setItem('chat_session_id', sid);
        localStorage.setItem('session_id', sid);

        if (isNewSession) {
          window.dispatchEvent(new CustomEvent('chat:session_created'));
        }
      }

      // ── Toggle message ──────────────────────────────────────────────────
      // Backend sends this right before the final deep-research report,
      // once the whole deep-research flow (any mid-flow clarifying
      // questions plus the report) has concluded. Mark the next response
      // as the report so the UI can offer a Word download for it.
      if (type === 'toggle') {
        if (data.ui_dr_toggle_off) {
          if (onToggleDeepSearch) {
            onToggleDeepSearch(false);
          }
          setThinkingText('');
          setThinkingSessionId(null);
          pendingDeepResearchReportRef.current = true;
        }
        return;
      }

      // ── Logs message ────────────────────────────────────────────────────
      // Streaming thinking/progress lines shown in the thinking indicator
      // while the agent is processing.
      if (type === 'logs') {
        const sid = data.session_id ?? localStorage.getItem('session_id') ?? '';
        if (!thinkingSessionId) setThinkingSessionId(sid);
        if (thinkingSessionId && thinkingSessionId !== sid) {
          setThinkingText('');
          setThinkingSessionId(sid);
        }
        const line = typeof data.content === 'string' ? data.content : '';
        setThinkingText((prev) => (prev ? `${prev}\n${line}` : line));
        setIsLoading(true);
        return;
      }

      // ── Clarification message ───────────────────────────────────────────
      // The agent needs more input before it can continue. Questions (and
      // their options) are fully backend-driven; Chat.tsx sends the answers
      // back as a new turn via the clarification panel.
      if (type === 'clarification') {
        setThinkingText('');
        setThinkingSessionId(null);
        setIsLoading(false);

        const questions: ClarificationQuestion[] = (Array.isArray(data.questions) ? data.questions : [])
          .filter((q: any) => q && typeof q.question === 'string' && q.question.trim())
          .map((q: any) => ({
            question: q.question,
            options: Array.isArray(q.options)
              ? q.options.filter((o: any) => typeof o === 'string' && o.trim())
              : [],
          }));
        if (questions.length > 0) {
          setPendingClarification(questions);
        } else {
          // Nothing usable came through: say so, rather than leaving a
          // spinner that just stops. Cleared by the next send.
          setError("The assistant needed more details, but its questions didn't come through. Please try again.");
        }
        return;
      }

      // ── Response message ────────────────────────────────────────────────
      // The final agent response. Clear thinking state, format and append
      // the message, then check if we need to refresh the sidebar for
      // a newly generated chat name.
      if (type === 'response') {
        setThinkingText('');
        setThinkingSessionId(null);

        const responseContent = toMessageContent(data);

        const files: ChatResponseFile[] | undefined = Array.isArray(data.files) ? data.files : undefined;

        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          content: responseContent,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          isMarkdown: true,
          isDeepResearchReport: pendingDeepResearchReportRef.current,
          files,
          turnId: data.turn_id,
        };
        pendingDeepResearchReportRef.current = false;

        setMessages((prev) => [...prev, assistantMessage]);
        setIsLoading(false);

        if (files && files.length > 0) {
          onFilesReceived?.(files);
        }

        // Once a brand-new chat's 2nd response comes in, there's enough
        // context for the backend to generate a real title — request it
        // directly rather than guessing a turn count and hoping the
        // backend named it on its own by now. Only for genuinely new
        // chats; a resumed session already has a name. Fire-and-forget:
        // refresh the sidebar only once the name actually exists.
        agentResponseCountRef.current += 1;
        if (agentResponseCountRef.current === 2 && isNewChatRef.current) {
          void generateChatTitle(data.session_id)
            .then((result) => {
              if (result) window.dispatchEvent(new CustomEvent('chat:session_created'));
            })
            .catch(() => { /* best-effort — sidebar just won't show a name yet */ });
        }

        return;
      }

      // A type this app doesn't know is ignored, silently: a new kind of
      // backend message must never show up as a reply, or end one that's
      // still coming.
      if (type) return;

      // ── Fallback ────────────────────────────────────────────────────────
      // Handles any response that doesn't have an explicit type field.
      {
        const responseContent = toMessageContent(data);

        const files: ChatResponseFile[] | undefined = Array.isArray(data.files) ? data.files : undefined;

        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          content: responseContent,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          isFormatted: true,
          isDeepResearchReport: pendingDeepResearchReportRef.current,
          files,
        };
        pendingDeepResearchReportRef.current = false;
        setMessages((prev) => [...prev, assistantMessage]);
        setIsLoading(false);

        if (files && files.length > 0) {
          onFilesReceived?.(files);
        }
      }
    } catch (err) {
      console.error('Error parsing message:', err);
      setIsLoading(false);
    }
  };

  // ── Safe send helper ──────────────────────────────────────────────────────
  // Handles all three WebSocket states: closed (reconnect), connecting
  // (queue the payload), and open (send immediately).
  const sendPayloadSafely = (payload: string, turnId: string) => {
    activeTurnIdRef.current = turnId;

    if (!WS_URL) {
      setError("WebSocket URL is not configured.");
      setIsLoading(false);
      return;
    }

    if (!ws.current || ws.current.readyState === READY.CLOSED) {
      // Fetch a fresh token right before opening — a stale one gets rejected
      // pre-open, which the browser only reports as a generic close event.
      void (async () => {
        const token = await getAccessToken();
        const socket = new WebSocket(WS_URL, ['auth', token]);
        ws.current = socket;

        socket.onopen = () => {
          setIsAssistantActive(true);
          setError("");
          socket.send(payload);
          (socket as any)._queuedPayload = undefined;
        };

        socket.onmessage = handleSocketMessage;

        socket.onerror = (e) => {
          console.warn("WebSocket error:", e);
          setError("WebSocket connection error.");
          setIsLoading(false);
        };

        socket.onclose = (e) => {
          reportUnexpectedClose(e);
          setIsAssistantActive(false);
        };
      })();
      return;
    }

    if (ws.current.readyState === READY.CONNECTING) {
      // Queue the payload — it will be sent once the socket opens
      (ws.current as any)._queuedPayload = payload;
      setIsAssistantActive(true);
      return;
    }

    if (ws.current.readyState === READY.OPEN) {
      ws.current.send(payload);
      setIsAssistantActive(true);
      return;
    }

    setError("WebSocket is not connected.");
    setIsLoading(false);
  };

  // ── Initialize WebSocket on mount ─────────────────────────────────────────
  // Opens the connection immediately so it's ready when the user sends
  // their first message. Cleans up on unmount.
  useEffect(() => {
    if (!WS_URL) {
      setError("WebSocket URL is not configured.");
      return;
    }

    // setMessages([]);

    let cancelled = false;
    let socket: WebSocket | null = null;

    void (async () => {
      // Fetch a fresh token right before opening — a stale one gets rejected
      // pre-open, which the browser only reports as a generic close event.
      const token = await getAccessToken();
      if (cancelled) return;

      socket = new WebSocket(WS_URL, ['auth', token]);
      console.log('[useWebSocket] Connecting to:', WS_URL);
      ws.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected');
        setIsAssistantActive(true);
        setError('');
        // Send any payload that was queued before the connection opened
        const anySocket = socket as any;
        if (anySocket._queuedPayload) {
          try {
            socket!.send(anySocket._queuedPayload);
          } finally {
            anySocket._queuedPayload = undefined;
          }
        }
      };

      socket.onmessage = handleSocketMessage;

      socket.onerror = (e) => {
        console.warn('WebSocket error (suppressed):', e);
      };

      socket.onclose = (e) => {
        reportUnexpectedClose(e);
        setIsAssistantActive(false);
      };
    })();

    return () => {
      cancelled = true;
      try { socket?.close(); } catch { }
      ws.current = null;
    };
  }, []);

  // ── Abandon the active turn ────────────────────────────────────────────────
  // Shared reset for every place the user moves on from whatever's in
  // flight: clicking Stop, starting a new chat, or switching to a different
  // history session. The backend never cancels in-flight work in any of
  // these cases — clearing activeTurnIdRef is what makes the eventual stale
  // message (logs/toggle/response) fail to match and get dropped instead of
  // leaking into whatever's on screen by the time it arrives (see
  // activeTurnIdRef's comment). Also clears the thinking indicator, since
  // that's equally stale in all three cases.
  const resetActiveTurn = () => {
    activeTurnIdRef.current = null;
    setThinkingText('');
    setThinkingSessionId(null);
    setIsLoading(false);
    pendingDeepResearchReportRef.current = false;
    setPendingClarification(null);
  };

  return {
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    error,
    setError,
    isAssistantActive,
    thinkingSessionId,
    thinkingText,
    setThinkingText,
    setThinkingSessionId,
    pendingClarification,
    setPendingClarification,
    latestUserMessageId,
    lastUserRequestAtRef,
    agentResponseCountRef,
    sendPayloadSafely,
    resetActiveTurn,
  };
};